import { Chip, Tabs } from '@medeloop/medeloop-components-library';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetStudyById, useGetStudyOrganizations } from 'src/api/studies-queries';
import { NavigateBack, SkeletonLine, TitleSubTitle } from 'src/components';

const BASE_STUDIES_URL = '/account/studies';

export function StudiesDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { studyId } = useParams();

  const { data, isLoading } = useGetStudyById(studyId ?? '');
  const { data: orgGeneralInfo } = useGetStudyOrganizations(studyId ?? '');

  return (
    <section className="flex flex-col">
      <div className="h-72  bg-dark-subdued-background -mb-10 border-b border-stroke-on-dark-subdued">
        <div className="max-w-right-section mx-auto px-6 xl:px-0">
          <NavigateBack to={BASE_STUDIES_URL} label="Studies" />
          <div className="flex flex-col h-20 gap-6">
            <div className="flex items-center justify-between">
              <TitleSubTitle
                title={data?.displayName ?? ''}
                subTitle={data?.name ?? ''}
                isLoading={isLoading}
              />
              <div className="flex items-center justify-center pl-4">
                {isLoading ? (
                  <div className="w-[150px]">
                    <SkeletonLine />
                  </div>
                ) : (
                  <span className="text-content-on-dark-default">
                    <Chip
                      className="border-purple-200 text-purple-200"
                      text={orgGeneralInfo?.organizationDisplayName ?? ''}
                      disabled
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between border rounded-md border-stroke-on-dark-subdued w-full p-4">
              <div className="flex basis-1/4 gap-1 flex-col items-start justify-center pr-4">
                {isLoading ? (
                  <div className="w-[80px]">
                    <SkeletonLine />
                  </div>
                ) : (
                  <>
                    <span className="text-sm">Expected time in the study</span>
                    <span className="text-content-on-dark-default">
                      {data?.expectedParticipantDuration ?? '-'}
                    </span>
                  </>
                )}
              </div>
              <div className="flex basis-1/4 gap-1 flex-col items-start justify-center pl-4 border-l border-stroke-on-dark-subdued">
                {isLoading ? (
                  <div className="w-[150px]">
                    <SkeletonLine />
                  </div>
                ) : (
                  <>
                    <span className="text-sm">Expected total participants</span>
                    <span className="text-content-on-dark-default">
                      {data?.expectedParticipants ?? '-'}
                    </span>
                  </>
                )}
              </div>
              <div className="flex basis-1/4 gap-1 flex-col items-start justify-center pl-4 border-l border-stroke-on-dark-subdued">
                {isLoading ? (
                  <div className="w-[150px]">
                    <SkeletonLine />
                  </div>
                ) : (
                  <>
                    <span className="text-sm">Start date</span>
                    <span className="text-content-on-dark-default">
                      {data?.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '-'}
                    </span>
                  </>
                )}
              </div>
              <div className="flex basis-1/4 gap-1 flex-col items-start justify-center pl-4 border-l border-stroke-on-dark-subdued">
                {isLoading ? (
                  <div className="w-[150px]">
                    <SkeletonLine />
                  </div>
                ) : (
                  <>
                    <span className="text-sm">Completion date</span>
                    <span className="text-content-on-dark-default">
                      {data?.startDate
                        ? new Date(data.completionDate).toISOString().split('T')[0]
                        : '-'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-right-section w-full mx-auto px-6 xl:px-0">
        <Tabs
          value={location.pathname.split('/').pop()}
          onValueChange={(value) => navigate(`${BASE_STUDIES_URL}/${studyId}/${value}`)}
        >
          <Tabs.List className="border-b-0">
            <Tabs.Trigger value="participants">
              Participants ({data?.numParticipants ?? 0})
            </Tabs.Trigger>
            <Tabs.Trigger value="researchers">Researchers ({0})</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="participants">
            <Outlet />
          </Tabs.Content>
          <Tabs.Content value="researchers">
            <Outlet />
          </Tabs.Content>
        </Tabs>
      </div>
    </section>
  );
}
